'use client';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@veneer/core';
import { env } from 'next-runtime-env';
import { useTranslation } from 'react-i18next';
import { FlowType } from '@storefront/app/flow-orchestrator/types';
import { useGetFaqs } from '@storefront/hooks/useGetFaqs';
import { useIntersection } from '@storefront/hooks/useIntersection';
import DataCollection from '@storefront/services/DataCollection';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import Accordion from '@storefront/shared-components/accordion/accordion';
import { forwardParams } from '@storefront/utils/queryParamsUtils';
import styles from './faq.module.scss';
import { IFaqType } from './faq.utils';
import ThemeProviderWrapper from '../../../ThemeProviderWrapper';
import { getSkuList } from '../../printer/utils/utils';
import { NamespacesI18n } from '@storefront/i18n';
import { useProductCancellationList } from '@storefront/hooks/useProductCancellationList';

interface FaqProps {
  searchParams: {
    skus: string;
    pdpFlow: FlowType;
  };
}

export function Faq({ searchParams }: Readonly<FaqProps>) {
  const [triggerAnalyticsOnce, setTriggerAnalyticsOnce] = useState(true);
  const [configuredFaqQuestion, setConfiguredFaqQuestion] = useState<
    IFaqType[]
  >([]);

  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);
  const skus = getSkuList(searchParams.skus);
  const { cancellationList, multipleProduct, hasThreeYearsCancellationDate } =
    useProductCancellationList(skus);
  const ref = useRef(null);
  const inViewport = useIntersection(ref, '0px');

  const flowType = searchParams?.pdpFlow;

  const faqs = useGetFaqs({
    flowType,
    cancellationList,
    multipleProduct,
    skus,
    hasThreeYearsCancellationSku: hasThreeYearsCancellationDate,
  });

  useEffect(() => {
    if (configuredFaqQuestion.length <= 0 && faqs.length > 0) {
      setConfiguredFaqQuestion(faqs);
    }
  }, [faqs]);

  useEffect(() => {
    const productAnalytics = uiEvents.printerConfigFAQSD;
    if (inViewport && triggerAnalyticsOnce) {
      DataCollection.sendEvent(productAnalytics);
      setTriggerAnalyticsOnce(false);
    }
  }, [inViewport]);

  const handleExpand = (_?: unknown, index?: number) => {
    setConfiguredFaqQuestion((state) => {
      const updatedData = [...state];
      updatedData[index ?? 0].expanded = true;
      const productAnalytics = uiEvents.printerConfigFAQAccordianExpanded;
      DataCollection.sendEvent({
        ...productAnalytics,
        controlDetail: updatedData[index ?? 0].id,
      });
      return updatedData;
    });
  };

  const handleCollapse = (_?: unknown, index?: number) => {
    if (index !== undefined) {
      setConfiguredFaqQuestion((state) => {
        const updatedData = [...state];
        updatedData[index].expanded = false;
        return updatedData;
      });
    }
  };

  const handleClickFaqs = async () => {
    const faqUrl = env('NEXT_PUBLIC_FULL_FAQ_URL');
    const urlWithParams = forwardParams(faqUrl);

    const productAnalytics = uiEvents.printerConfigFAQBC;

    await DataCollection.sendEvent({
      ...productAnalytics,
      controlDetail: urlWithParams,
    });
    window.open(urlWithParams, '_self');
  };

  return (
    <div className={styles['container']} ref={ref} id="faq-container">
      <div data-testid={'header'} className={styles['header']} id="faq-header">
        {t('faq.header')}
      </div>
      <ThemeProviderWrapper>
        <Accordion
          behavior="singleExpand"
          items={configuredFaqQuestion}
          customAppearance="chevron"
          onCollapse={handleCollapse}
          onExpand={handleExpand}
          id="info-accordian"
          data-testid="infoAccordion"
        />
        <div className={styles['faq-section']}>
          <Button
            id="faq-button"
            onClick={handleClickFaqs}
            data-testid={'external-link-icon'}
          >
            {t('faq.redirectText')}
          </Button>
        </div>
      </ThemeProviderWrapper>
    </div>
  );
}

export default Faq;
